import { useState } from 'react'
import { Box, Button, Group, Select, Stack, Text } from '@grovus/design-system'
import { axisCopy, DICTIONARY_COPY, type Lang } from '../i18n'
import { fill } from './format'
import type { TagChange } from './api'
import { DictionaryPicker } from './DictionaryPicker'
import type { Dictionary } from './useDictionary'
import type { Axis, HypothesisValue } from '../hypotheses/dictionaryApi'

/** The version letters a creative can carry inside its hypothesis (GRO-159). */
const LETTERS = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))
const AXES: readonly Axis[] = ['idea', 'visual', 'message', 'offer']
const FIELD: Record<Axis, keyof TagChange> = { idea: 'ideaId', visual: 'visualId', message: 'messageId', offer: 'offerId' }

/** A line the bar settles with: the confirmation, or the error a failed write left. */
export interface AssignNotice {
  role: 'status' | 'alert'
  text: string
}

/** The value each field holds, or nothing. */
export type Fields = Record<Axis, HypothesisValue | null>

const EMPTY: Fields = { idea: null, visual: null, message: null, offer: null }

/**
 * The body of «Применить»: the filled fields only, so an empty field touches nothing, or
 * `null` when no field holds a value.
 */
export function filledChange(fields: Fields): TagChange | null {
  const change: TagChange = {}
  for (const axis of AXES) {
    const value = fields[axis]
    if (value !== null) change[FIELD[axis]] = value.id
  }
  return Object.keys(change).length === 0 ? null : change
}

interface Props {
  lang: Lang
  /** The number of picked rows; the screen renders the bar only while it is positive. */
  count: number
  /** The rows answer the current scope and no write is out for this company. */
  enabled: boolean
  /** The options of each axis: the screen's own reads, which the chips share. */
  options: Record<Axis, Dictionary>
  /** Sends the filled fields as one change. */
  onApply: (change: TagChange) => void
  onClearIdea: () => void
  /** Clears the visual, the message and the offer, and keeps the idea. */
  onClearTags: () => void
  /** "Create" was chosen in a field: resolves with the created value, or `null` on a refusal. */
  onCreate: (axis: Axis, name: string) => Promise<HypothesisValue | null>
  /** Every picked row carries a hypothesis, so it can carry a version letter (GRO-159). */
  versionable: boolean
  /** Sets a version letter on the picked rows, or clears it with `null`. */
  onSetVersion: (version: string | null) => void
  /** Opens the merge of the picked rows into one (GRO-161); offered from two rows. */
  onMerge: () => void
  onCancel: () => void
  /** The line the last write left, shown in the bar while it is up. */
  notice: AssignNotice | null
}

/**
 * The bar of the selection (GRO-172), fixed to the bottom edge of the content while a row is
 * picked: a field per axis, applied together with «Применить», the two clears, the version
 * letter (GRO-159) and the merge (GRO-161).
 *
 * A field holds the value object it was given, from the options or from a create, and keeps
 * it while its axis reloads after a dictionary write: the picker takes its labels from its
 * values, which stay empty until the new read answers. The field lets the value go only when
 * a read that answered no longer carries it.
 */
export function SelectionBar({
  lang, count, enabled, options, onApply, onClearIdea, onClearTags, onCreate, versionable, onSetVersion, onMerge,
  onCancel, notice,
}: Props) {
  const copy = DICTIONARY_COPY[lang]
  const [held, setHeld] = useState<Fields>(EMPTY)
  const [letter, setLetter] = useState<string | null>(null)
  const versioning = enabled && versionable
  const fields = { ...EMPTY }
  for (const axis of AXES) {
    const value = held[axis]
    const { fresh, values } = options[axis]
    if (value !== null && (!fresh || values.some((candidate) => candidate.id === value.id))) fields[axis] = value
  }
  const change = filledChange(fields)
  const settled = AXES.every((axis) => fields[axis] === null || options[axis].fresh)
  const choose = (axis: Axis, id: string | null) => {
    const value = id === null ? null : options[axis].values.find((candidate) => candidate.id === id) ?? null
    setHeld((prev) => ({ ...prev, [axis]: value }))
  }
  const create = async (axis: Axis, name: string) => {
    const value = await onCreate(axis, name)
    if (value !== null) setHeld((prev) => ({ ...prev, [axis]: value }))
  }

  return (
    <Box className="grv-selection-bar" px={16} py={12} data-testid="selection-bar">
      <Stack gap={8}>
        <Group gap="8px 12px" wrap="wrap" align="center">
          <Text size="sm" fw={500} data-testid="selection-count">{fill(copy.selected, { c: count })}</Text>
          {AXES.map((axis) => {
            const lines = axisCopy(copy, axis)
            const { fresh, values } = options[axis]
            const value = fields[axis]
            return (
              <DictionaryPicker
                key={axis}
                lang={lang}
                axis={axis}
                aria-label={lines.picker}
                placeholder={lines.pickerPlaceholder}
                values={fresh || value === null ? values : [value]}
                value={value?.id ?? null}
                onChoose={(id) => choose(axis, id)}
                onCreate={(name) => { void create(axis, name) }}
                disabled={!enabled || !fresh}
                w={180}
                data-testid={`selection-field-${axis}`}
              />
            )
          })}
          <Button
            size="compact-sm"
            disabled={!enabled || change === null || !settled}
            onClick={() => { if (change !== null) onApply(change) }}
            data-testid="selection-apply"
          >
            {copy.apply}
          </Button>
          <Button size="compact-sm" variant="default" disabled={!enabled} onClick={onClearIdea}>
            {copy.clear}
          </Button>
          <Button size="compact-sm" variant="default" disabled={!enabled} onClick={onClearTags}>
            {copy.clearTags}
          </Button>
          <Group gap={8} wrap="wrap" align="center" data-testid="version-controls">
            <Select
              aria-label={copy.versionPicker}
              placeholder={copy.versionPicker}
              data={LETTERS}
              value={letter}
              onChange={setLetter}
              disabled={!versioning}
              w={100}
              data-testid="version-picker"
            />
            <Button
              size="compact-sm"
              disabled={!versioning || letter === null}
              onClick={() => { if (letter !== null) onSetVersion(letter) }}
            >
              {copy.setVersion}
            </Button>
            <Button size="compact-sm" variant="default" disabled={!versioning} onClick={() => onSetVersion(null)}>
              {copy.clearVersion}
            </Button>
          </Group>
          <Button size="compact-sm" variant="default" disabled={!enabled || count < 2} onClick={onMerge} data-testid="merge-open">
            {copy.merge}
          </Button>
          <Button size="compact-sm" variant="subtle" ml="auto" disabled={!enabled} onClick={onCancel} data-testid="selection-cancel">
            {copy.cancelSelection}
          </Button>
        </Group>
        {!versionable && (
          <Text size="xs" className="grv-muted" data-testid="version-needs-hypothesis">
            {copy.versionNeedsHypothesis}
          </Text>
        )}
        {options.idea.phase === 'ready' && options.idea.values.length === 0 && (
          <Text size="xs" className="grv-muted">{copy.noOptions}</Text>
        )}
        {AXES.filter((axis) => options[axis].phase === 'failed').map((axis) => (
          <Group gap="xs" key={axis}>
            <Text size="xs">{axisCopy(copy, axis).optionsFailed}</Text>
            <Button size="compact-xs" variant="default" onClick={options[axis].retry}>{copy.retry}</Button>
          </Group>
        ))}
        {notice !== null && (
          <Text size="sm" role={notice.role} data-testid="assign-notice">{notice.text}</Text>
        )}
        <Text size="xs" className="grv-muted" data-testid="assign-scope">{copy.scopeLine}</Text>
      </Stack>
    </Box>
  )
}
